/**
 * Phone-as-username helpers.
 *
 * Auth convention, locked in the schema:
 *   email = replace(phone, '+', '') || '@staff.lafolie.internal'
 *   +971501230001  ->  971501230001@staff.lafolie.internal
 *
 * Coaches type their number in whatever form they know it. In the UAE that is
 * usually the local 05x form. We normalise everything to E.164 digits with the
 * 971 country code and no plus sign before building the synthetic email.
 */

export const AUTH_EMAIL_DOMAIN = 'staff.lafolie.internal'
export const DEFAULT_COUNTRY_CODE = '971'

/** Strip everything that is not a digit. Handles spaces, dashes, brackets, plus. */
function digitsOnly(input: string): string {
  return input.replace(/\D+/g, '')
}

/**
 * Normalise a typed phone number to bare international digits, no plus.
 *
 *   "+971 50 123 0001" -> "971501230001"
 *   "0501230001"       -> "971501230001"
 *   "501230001"        -> "971501230001"
 *   "00971501230001"   -> "971501230001"
 *
 * Returns null when the input cannot be a UAE mobile number.
 */
export function normalizePhone(input: string): string | null {
  let d = digitsOnly(input)
  if (!d) return null

  // 00 international prefix
  if (d.startsWith('00')) d = d.slice(2)

  // Already carries the country code
  if (d.startsWith(DEFAULT_COUNTRY_CODE)) {
    const rest = d.slice(DEFAULT_COUNTRY_CODE.length)
    // A UAE mobile national number is 9 digits and starts with 5.
    if (rest.length === 9 && rest.startsWith('5')) return DEFAULT_COUNTRY_CODE + rest
    // Landlines and other lengths still pass through, we do not police them.
    if (rest.length >= 8 && rest.length <= 10) return DEFAULT_COUNTRY_CODE + rest
    return null
  }

  // Local trunk form: 0501230001
  if (d.startsWith('0')) {
    const rest = d.slice(1)
    if (rest.length >= 8 && rest.length <= 10) return DEFAULT_COUNTRY_CODE + rest
    return null
  }

  // Bare national form: 501230001
  if (d.length >= 8 && d.length <= 10) return DEFAULT_COUNTRY_CODE + d

  // Some other country's number typed in full. Accept it as-is if plausible.
  if (d.length >= 11 && d.length <= 15) return d

  return null
}

/** E.164 display form, e.g. "+971501230001". */
export function toE164(input: string): string | null {
  const n = normalizePhone(input)
  return n ? `+${n}` : null
}

/** Build the synthetic auth email the schema expects. */
export function toAuthEmail(input: string): string | null {
  const n = normalizePhone(input)
  return n ? `${n}@${AUTH_EMAIL_DOMAIN}` : null
}

/** Reverse of toAuthEmail, used to show a friendly identity after login. */
export function phoneFromAuthEmail(email: string | null | undefined): string | null {
  if (!email) return null
  const [local, domain] = email.split('@')
  if (domain !== AUTH_EMAIL_DOMAIN) return null
  return `+${local}`
}

/** Readable grouping for UAE mobiles: "+971 50 123 0001". */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return ''
  const d = digitsOnly(phone)
  if (d.startsWith(DEFAULT_COUNTRY_CODE) && d.length === 12) {
    const rest = d.slice(3)
    return `+971 ${rest.slice(0, 2)} ${rest.slice(2, 5)} ${rest.slice(5)}`
  }
  return phone
}

/** Message shown under the phone field when the number cannot be parsed. */
export const PHONE_HELP =
  'Enter your mobile number, for example 050 123 0001 or +971 50 123 0001.'
